import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getTrailAnalytics } from '../../../services/teacherAnalyticsService';

const TrailAnalytics = ({ trailId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trailId) return;
    const fetchAnalytics = async () => {
      setLoading(true);
      const { analytics: data, error: err } = await getTrailAnalytics(trailId);
      if (err) {
        setError(err);
      } else {
        setAnalytics(data);
      }
      setLoading(false);
    };
    fetchAnalytics();
  }, [trailId]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
          Analytics
        </h3>
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
          Analytics
        </h3>
        <div className="text-center py-6">
          <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Failed to load analytics</p>
        </div>
      </div>
    );
  }

  const totalAttempts = analytics?.totalAttempts ?? 0;
  const completedCount = analytics?.completedCount ?? 0;
  const inProgressCount = analytics?.inProgressCount ?? 0;
  const completionRate = analytics?.completionRate ?? 0;
  const avgQuizScore = analytics?.avgQuizScore ?? 0;
  const notStartedCount = Math.max(0, totalAttempts - completedCount - inProgressCount);

  return (
    <div className="bg-card rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
        <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
        Analytics
      </h3>

      <div className="space-y-4">
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{totalAttempts}</p>
            <p className="text-xs text-muted-foreground">Total Attempts</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-success">{completedCount}</p>
            <p className="text-xs text-muted-foreground">Completions</p>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Completion Rate</span>
            <span className="font-medium text-foreground">{completionRate}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>

        {/* Avg Quiz Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Avg. Quiz Score</span>
            <span className="font-medium text-foreground">{avgQuizScore}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-accent h-2 rounded-full transition-all"
              style={{ width: `${avgQuizScore}%` }}
            />
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Status Breakdown</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-muted-foreground">Completed</span>
              </div>
              <span className="font-medium text-foreground">{completedCount}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-accent" />
                <span className="text-muted-foreground">In Progress</span>
              </div>
              <span className="font-medium text-foreground">{inProgressCount}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                <span className="text-muted-foreground">Not Started</span>
              </div>
              <span className="font-medium text-foreground">{notStartedCount}</span>
            </div>
          </div>
        </div>

        {/* No data state */}
        {totalAttempts === 0 && (
          <div className="text-center py-4">
            <Icon name="Activity" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No student activity yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailAnalytics;